import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

function defaultValueForProp(propSchema, propName, courseSlug) {
  if (propSchema.default !== undefined) {
    return propSchema.default;
  }
  if (propName === 'course_slug' && courseSlug) {
    return courseSlug;
  }
  const type = propSchema.type;
  if (type === 'boolean') return false;
  if (type === 'integer' || type === 'number') return '';
  if (type === 'array') return '[]';
  if (type === 'object') return '{}';
  return '';
}

function buildDefaults(inputSchema, courseSlug) {
  const properties = inputSchema?.properties || {};
  const defaults = {};
  Object.entries(properties).forEach(([name, schema]) => {
    defaults[name] = defaultValueForProp(schema || {}, name, courseSlug);
  });
  return defaults;
}

function coerceArguments(params, inputSchema) {
  const properties = inputSchema?.properties || {};
  const coerced = {};

  Object.entries(params).forEach(([name, raw]) => {
    const schema = properties[name] || {};
    const type = schema.type;

    if (raw === '' || raw === undefined || raw === null) {
      if ((inputSchema.required || []).includes(name)) {
        coerced[name] = raw === '' ? '' : raw;
      }
      return;
    }

    if (type === 'boolean') {
      coerced[name] = raw === true || raw === 'true';
      return;
    }
    if (type === 'integer') {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) coerced[name] = n;
      return;
    }
    if (type === 'number') {
      const n = parseFloat(raw);
      if (!Number.isNaN(n)) coerced[name] = n;
      return;
    }
    if (type === 'array' || type === 'object') {
      if (typeof raw === 'string') {
        try {
          coerced[name] = JSON.parse(raw);
        } catch {
          coerced[name] = raw;
        }
      } else {
        coerced[name] = raw;
      }
      return;
    }
    coerced[name] = raw;
  });

  return coerced;
}

function SchemaField({ name, schema, value, required, onChange }) {
  const type = schema.type || 'string';
  const description = schema.description || '';
  const enumValues = schema.enum;

  let control;
  if (enumValues && Array.isArray(enumValues)) {
    control = (
      <select value={value ?? ''} onChange={(e) => onChange(name, e.target.value)}>
        <option value="">— select —</option>
        {enumValues.map((opt) => (
          <option key={String(opt)} value={opt}>{String(opt)}</option>
        ))}
      </select>
    );
  } else if (type === 'boolean') {
    control = (
      <label className="mcp-checkbox">
        <input
          type="checkbox"
          checked={value === true || value === 'true'}
          onChange={(e) => onChange(name, e.target.checked)}
        />
        {value === true || value === 'true' ? 'true' : 'false'}
      </label>
    );
  } else if (type === 'integer' || type === 'number') {
    control = (
      <input
        type="number"
        step={type === 'integer' ? 1 : 'any'}
        value={value ?? ''}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={schema.default !== undefined ? String(schema.default) : ''}
      />
    );
  } else if (type === 'array' || type === 'object') {
    control = (
      <textarea
        rows={3}
        value={typeof value === 'string' ? value : JSON.stringify(value ?? (type === 'array' ? [] : {}), null, 2)}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={type === 'array' ? '[]' : '{}'}
      />
    );
  } else {
    const isLong = name.includes('query') || name.includes('sql') || (description && description.length > 80);
    if (isLong) {
      control = (
        <textarea
          rows={3}
          value={value ?? ''}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={schema.default !== undefined ? String(schema.default) : ''}
        />
      );
    } else {
      control = (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(name, e.target.value)}
          placeholder={schema.default !== undefined ? String(schema.default) : ''}
        />
      );
    }
  }

  return (
    <div className="mcp-field">
      <label htmlFor={`mcp-param-${name}`}>
        {name}
        {required && <span className="mcp-required">*</span>}
        {type && <span className="mcp-type">{type}</span>}
      </label>
      {description && <p className="mcp-field-desc">{description}</p>}
      <div className="mcp-field-control" id={`mcp-param-${name}`}>{control}</div>
    </div>
  );
}

function McpPlayground({ apiBaseUrl, courseSlug, onClose }) {
  const [tools, setTools] = useState([]);
  const [toolsError, setToolsError] = useState(null);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [selectedTool, setSelectedTool] = useState('');
  const [params, setParams] = useState({});
  const [calling, setCalling] = useState(false);
  const [callError, setCallError] = useState(null);
  const [result, setResult] = useState(null);

  const loadTools = useCallback(async () => {
    setToolsLoading(true);
    setToolsError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/mcp/tools`);
      if (response.data.error) {
        setToolsError(response.data.error);
        setTools([]);
        return;
      }
      const list = response.data.tools || [];
      setTools(list);
      if (list.length > 0) {
        setSelectedTool((prev) => prev || list[0].name);
      }
    } catch (error) {
      setToolsError(error.response?.data?.detail || error.message);
      setTools([]);
    } finally {
      setToolsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    loadTools();
  }, [loadTools]);

  const tool = useMemo(
    () => tools.find((t) => t.name === selectedTool) || null,
    [tools, selectedTool]
  );

  useEffect(() => {
    if (!tool) {
      setParams({});
      return;
    }
    setParams(buildDefaults(tool.inputSchema || {}, courseSlug));
    setResult(null);
    setCallError(null);
  }, [tool, courseSlug]);

  const handleParamChange = (name, value) => {
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const handleToolChange = (name) => {
    setSelectedTool(name);
  };

  const handleCall = async () => {
    if (!tool || calling) return;
    setCalling(true);
    setCallError(null);
    setResult(null);
    try {
      const argumentsPayload = coerceArguments(params, tool.inputSchema || {});
      const response = await axios.post(
        `${apiBaseUrl}/mcp/tools/${encodeURIComponent(tool.name)}/call`,
        { arguments: argumentsPayload }
      );
      if (response.data.error) {
        setCallError(response.data.error);
        setResult(response.data);
      } else {
        setResult(response.data.result);
      }
    } catch (error) {
      setCallError(error.response?.data?.detail || error.message);
    } finally {
      setCalling(false);
    }
  };

  const properties = tool?.inputSchema?.properties || {};
  const required = new Set(tool?.inputSchema?.required || []);
  const propertyNames = Object.keys(properties);

  return (
    <div className="mcp-panel">
      <div className="mcp-panel-header">
        <span>MCP Playground</span>
        <div className="mcp-panel-header-actions">
          <button type="button" onClick={loadTools} disabled={toolsLoading} title="Refresh tool list">
            Refresh
          </button>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>

      <div className="mcp-panel-body">
        <section className="mcp-section">
          <label htmlFor="mcp-tool-select">Tool</label>
          {toolsLoading ? (
            <p className="mcp-muted">Loading tools from MCP server…</p>
          ) : toolsError ? (
            <p className="mcp-error">{toolsError}</p>
          ) : tools.length === 0 ? (
            <p className="mcp-muted">No tools available.</p>
          ) : (
            <select
              id="mcp-tool-select"
              value={selectedTool}
              onChange={(e) => handleToolChange(e.target.value)}
            >
              {tools.map((t) => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
          )}
          {tool?.description && (
            <p className="mcp-tool-desc">{tool.description}</p>
          )}
        </section>

        {tool && (
          <section className="mcp-section">
            <h4>Parameters</h4>
            {propertyNames.length === 0 ? (
              <p className="mcp-muted">This tool takes no parameters.</p>
            ) : (
              propertyNames.map((name) => (
                <SchemaField
                  key={name}
                  name={name}
                  schema={properties[name] || {}}
                  value={params[name]}
                  required={required.has(name)}
                  onChange={handleParamChange}
                />
              ))
            )}
            <button
              type="button"
              className="mcp-call-button"
              onClick={handleCall}
              disabled={calling || toolsLoading}
            >
              {calling ? 'Calling…' : 'Call tool'}
            </button>
          </section>
        )}

        {(callError || result !== null) && (
          <section className="mcp-section mcp-result-section">
            <h4>Result</h4>
            {callError && <p className="mcp-error">{callError}</p>}
            {result !== null && (
              <pre className="mcp-result">
                {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
              </pre>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default McpPlayground;
